import { NgModule } from '@angular/core';
import { NavigationError, Router, RouterModule, Routes, UrlMatcher, UrlMatchResult, UrlSegment } from '@angular/router';
import rg4js from 'raygun4js';

import { MainComponent } from './assessment-app/pages/main/main.component';
import { StartPageComponent } from './assessment-app/pages/start-page/start-page.component';
import { ThankYouComponent } from './assessment-app/pages/thank-you/thank-you.component';
import { InboxListComponent } from './components/inbox-list/inbox-list.component';
import { SentListComponent } from './components/sent-list/sent-list.component';
import { authenticatedGuard, partialAuthenticatedGuard } from './core/guards/authenticated.guard';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { MfaComponent } from './pages/login/mfa/mfa.component';
import { MattersPageComponent } from './pages/matters-page/matters-page.component';
import { CanDeactivateMessageComposeGuard } from './pages/message-compose/can-deactivate-message-compose.guard';
import { MessageComposeComponent } from './pages/message-compose/message-compose.component';
import { MessagesComponent } from './pages/messages/messages.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { ViewMatterComponent } from './pages/view-matter/view-matter.component';
import { ViewMessageComponent } from './pages/view-message/view-message.component';

const routes: Routes = [
  {
    matcher: MatchInsensitive('home'),
    component: HomeComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Client Portal'
    }
  },
  {
    matcher: MatchInsensitive('mfa'),
    component: MfaComponent
  },
  {
    matcher: MatchInsensitive('mfa-setup'),
    component: MfaComponent,
    canActivate: [partialAuthenticatedGuard]
  },
  {
    matcher: MatchInsensitive('mfa-remove'),
    component: MfaComponent,
    canActivate: [authenticatedGuard]
  },
  {
    matcher: MatchInsensitive('login'),
    component: LoginComponent
  },
  {
    matcher: MatchInsensitive('forgot-password'),
    component: ForgotPasswordComponent
  },
  {
    matcher: MatchInsensitive('reset-password'),
    component: ResetPasswordComponent
  },
  {
    matcher: MatchInsensitive('change-password'),
    component: ChangePasswordComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Change Password'
    }
  },
  {
    matcher: MatchInsensitive('inbox'),
    component: InboxListComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Inbox'
    }
  },
  {
    matcher: MatchInsensitive('sent'),
    component: SentListComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Sent'
    }
  },
  {
    matcher: MatchInsensitive('messages'),
    component: MessagesComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Search Messages'
    }
  },
  {
    matcher: MatchInsensitive('messages/:id'),
    component: ViewMessageComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Message'
    }
  },
  {
    matcher: MatchInsensitive('message-compose'),
    component: MessageComposeComponent,
    canActivate: [authenticatedGuard],
    canDeactivate: [CanDeactivateMessageComposeGuard],
    data: {
      title: 'New Message'
    }
  },
  {
    matcher: MatchInsensitive('privacy-policy'),
    component: PrivacyPolicyComponent,
    data: {
      title: 'Data Security, Privacy and Cookies Policy'
    }
  },
  {
    matcher: MatchInsensitive('matters'),
    component: MattersPageComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Search Matters'
    }
  },
  {
    matcher: MatchInsensitive('matters/:id'),
    component: ViewMatterComponent,
    canActivate: [authenticatedGuard],
    data: {
      title: 'Matter'
    }
  },
  {
    matcher: MatchInsensitive('assessment'),
    component: StartPageComponent,
    data: {
      title: 'Assessment'
    }
  },
  {
    matcher: MatchInsensitive('assessment/:form/:page'),
    component: MainComponent,
  },
  {
    matcher: MatchInsensitive('assessment/thank-you'),
    component: ThankYouComponent,
  },
  {
    matcher: MatchInsensitive('assessment/**'),
    redirectTo: 'assessment'
  },
  {
    path: '404',
    component: NotFoundComponent,
    data: {
      title: '404 Not Found'
    }
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      bindToComponentInputs: true
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor(private router: Router) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        rg4js('send', {
          error: event.error
        });
      }
    });
  }
}

function MatchInsensitive(path: string): UrlMatcher {
  const pathSegments = path.split('/').filter(s => s.length > 0);


  return (segments: UrlSegment[]): UrlMatchResult | null => {
    if (segments.length !== pathSegments.length) {
      return null;
    }

    const posParams: { [key: string]: UrlSegment } = {};
    const consumed: UrlSegment[] = [];

    for (let i = 0; i < pathSegments.length; i++) {
      // PathPart is the part of the route path (e.g., 'messages' or ':id')
      // urlSegment is the "actual" segment from the URL (e.g., 'messages' or '123')
      const pathPart = pathSegments[i];
      const urlSegment = segments[i];

      // check for param (i.e ':id')
      // we cant match param to route as the urlSegment is the actual value
      // so just go off vibes bro
      if (pathPart.startsWith(':')) {
        // Extract the parameter name from the path part (e.g., 'id' from ':id')
        const paramName = pathPart.substring(1);
        // add our url segment to the params + consumed
        posParams[paramName] = urlSegment;
        consumed.push(urlSegment);
      } else {
        // match "static" routey bit case insensitively
        if (pathPart.toLowerCase() !== urlSegment.path.toLowerCase()) {
          return null;
        }
        consumed.push(urlSegment);
      }
    }

    return { consumed, posParams };
  };

};
